/**
 * Pure workspace-selection logic, split out of workspace-context.tsx so it
 * can be unit-tested under node's plain test runner (see
 * apps/web/test/decision-bar.test.ts's header comment for why:
 * workspace-context.tsx imports `@/lib/api.ts`'s useApiQuery, which reads
 * `import.meta.env` at module scope and throws outside a Vite runtime —
 * this file has no such import, so `tsx --test` can exercise it directly).
 *
 * BUG-068: this is the exact derivation logic Kanban, Calendar, and
 * Social each used to duplicate locally (with their own selectedId and
 * sessionStorage key) — now centralized so all three consumers agree.
 */

export interface WorkspaceOption {
  id: number;
  name: string;
  slug: string;
}

/** selectedId === 0 ("All workspaces") is always valid; otherwise the id
 * must still be in the fetched list — a workspace can disappear (removed,
 * membership revoked) between when it was persisted and when it's read. */
export function isValidSelection(
  workspaces: readonly WorkspaceOption[],
  selectedId: number | null
): boolean {
  return selectedId === 0 || workspaces.some((w) => w.id === selectedId);
}

/** The exact derivation every view consuming this context needs: the
 * resolved single workspace object, falling back to the first workspace
 * when selectedId isn't a valid specific selection (unset, stale, or 0/
 * "All workspaces" — callers needing the All-workspaces case distinctly
 * check `allWorkspaces` separately). */
export function resolveActiveWorkspace(
  workspaces: readonly WorkspaceOption[],
  selectedId: number | null
): WorkspaceOption | undefined {
  return workspaces.find((w) => w.id === selectedId) ?? workspaces[0];
}

/** BUG-137: the selection in force. While a piece is open it pins its own
 * workspace, which overrides the stored selection (including "All
 * workspaces") without overwriting it, so leaving the piece restores it. */
export function effectiveSelection(
  selectedId: number | null,
  pinnedId: number | null
): number | null {
  return pinnedId ?? selectedId;
}

/** BUG-137: queries that read one piece's rows. They are RLS-scoped to the
 * active workspace like the lists are, so a workspace switch must refetch
 * them too. `piece-locate` is membership-scoped and excluded: refetching it
 * would re-run the BUG-071 recovery. */
export function isPieceScopedQueryKey(key: readonly unknown[]): boolean {
  const head = key[0];
  if (typeof head !== "string") return false;
  return head === "piece" || (head.startsWith("piece-") && head !== "piece-locate");
}
